import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Mail, KeyRound, ArrowRight } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No pudimos iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="hidden md:flex md:w-1/2 relative items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-70">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/07d2de6a-497a-4254-89cb-2b4d35aeca72/images/ee8dbac5f03b8709cfb0720b82e8a904fb6d3968ed73363b296f3a0fefc044b8.png"
            alt=""
            className="w-full h-full object-cover mix-blend-multiply"
          />
        </div>
        <div className="relative z-10 text-center px-12">
          <p className="font-dmsans text-xs tracking-[0.25em] uppercase text-ink-600">Trackeate</p>
          <h1 className="font-outfit text-4xl lg:text-5xl text-ink-900 mt-3 leading-tight max-w-md">
            Conocé tus patrones,<br />habitá tu bienestar.
          </h1>
          <p className="font-dmsans text-ink-600 mt-5 max-w-sm mx-auto leading-relaxed">
            Un registro diario, claro y sin fricción. Descubrí cómo se relacionan tu sueño, tu cuerpo y tu mente.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="md:hidden text-center mb-8">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-500 text-white font-outfit text-lg mb-3">t</div>
            <h2 className="font-outfit text-3xl text-ink-900">Bienvenido</h2>
            <p className="font-dmsans text-sm text-ink-600 mt-1">Tu espacio de autoconocimiento</p>
          </div>
          <div className="hidden md:block mb-8">
            <h2 className="font-outfit text-3xl text-ink-900">Iniciá sesión</h2>
            <p className="font-dmsans text-sm text-ink-600 mt-1">Tu bienestar continúa donde lo dejaste.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="tk-label mb-1.5 block">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-600/60" />
                <input
                  type="email"
                  className="tk-input pl-11"
                  placeholder="tu@email.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <label className="tk-label mb-1.5 block">Contraseña</label>
              <div className="relative">
                <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-600/60" />
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="tk-input pl-11 tracking-[0.4em]"
                  placeholder="• • • •"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value.replace(/\D/g, ""))}
                  required
                  maxLength={12}
                  data-testid="login-password"
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-wellness-critical bg-wellness-critical/5 border border-wellness-critical/20 rounded-2xl px-4 py-2.5" data-testid="login-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="tk-btn-primary w-full" data-testid="login-submit">
              {loading ? "Entrando…" : <>Entrar <ArrowRight size={16} /></>}
            </button>

            <div className="flex items-center justify-between text-sm pt-2">
              <Link to="/forgot-password" className="text-ink-600 hover:text-sage-600 transition" data-testid="login-forgot-link">
                Olvidé mi contraseña
              </Link>
              <Link to="/register" className="text-sage-600 hover:text-sage-700 font-medium transition" data-testid="login-register-link">
                Crear cuenta
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
